// Where a document link (#/doc/<kind>/<id>) opens, for these roles: the place, and what to show in
// it. A place the person doesn't open, or a document the server won't give them, is said in words;
// they stay where they are. The server still checks every read.

import type { DocRef } from './doc-link.ts';

export type DocTarget =
  | { view: 'customers'; customerId: string; focus: string }
  | { view: 'review'; jobId: string }
  | { view: 'books'; part: 'ledger' | 'payroll'; focus: string }
  | { error: string };

type Call = <T>(action: string, input: unknown) => Promise<T>;
/** Whether what the person holds opens a place (Nav's canSee), and Books' Payroll part (seesPayroll). */
export interface DocRights {
  place: (view: 'customers' | 'review' | 'books') => boolean;
  payroll: boolean;
}

export async function locateDoc(d: DocRef, call: Call, may: DocRights): Promise<DocTarget> {
  const cant = (what: string) => ({ error: `${what} can’t be opened here.` });
  if (d.kind === 'journal-entry')
    return may.place('books') ? { view: 'books', part: 'ledger', focus: d.id } : cant('That journal entry');
  if (d.kind === 'pay-period')
    return may.place('books') && may.payroll
      ? { view: 'books', part: 'payroll', focus: d.id }
      : cant('That pay period');
  if (d.kind === 'payment') {
    if (!may.place('customers')) return cant('That payment');
    try {
      const p = await call<{ customerId: string }>('payments.get', { id: d.id });
      return { view: 'customers', customerId: p.customerId, focus: d.id };
    } catch {
      return cant('That payment');
    }
  }
  let inv: { number: string; status: string; customerId?: string; jobId?: string | null };
  try {
    inv = await call('invoices.get', { id: d.id });
  } catch {
    return cant('That invoice');
  }
  // A draft is edited at Review on its job (the Money tab lists no drafts, QA-384).
  if (inv.status === 'draft')
    return inv.jobId && may.place('review')
      ? { view: 'review', jobId: inv.jobId }
      : { error: `${inv.number} is a draft: it’s edited at Review, on its job, which you don’t open.` };
  if (!inv.customerId || !may.place('customers')) return cant(inv.number);
  return { view: 'customers', customerId: inv.customerId, focus: d.id };
}
